import { useMemo, useRef, useState } from "react";
import { toneColor, type Tone } from "@/design/primitives";

/**
 * Grafy provozu strojů. Kreslí se ručně do SVG, protože jde o dvě velmi
 * konkrétní podoby (hodinová osa přes celý týden a strop tarifu), na které
 * je knihovna zbytečná — a hlavně proto, že barvy musí jít ze sémantických
 * tokenů, aby fungoval světlý i tmavý motiv.
 */

/**
 * Pořadí tónů pro série. Kritické červené je až na konci: u stroje znamená
 * „vyčerpaný limit", takže by v grafu mátlo, kdyby ho dostal třetí worker.
 */
export const SERIES_TONES: Tone[] = ["cyan", "violet", "amber", "green", "red"];
export const seriesTone = (index: number): Tone =>
  SERIES_TONES[index % SERIES_TONES.length] ?? "cyan";

export type ActivitySeries = {
  id: string;
  name: string;
  tone: Tone;
  /** Minuty práce v každé hodině okna, 0–60. */
  hourly: number[];
  /** Popisek pod jménem — tarif, souhrn hodin. */
  note?: string;
};

const DAY_NAMES = ["ne", "po", "út", "st", "čt", "pá", "so"];
const fmtHours = (h: number) => `${h.toFixed(1).replace(".", ",")} h`;

/**
 * Hodinová osa: jeden pruh na stroj, výška sloupce = minuty práce v té hodině.
 * Prostoj je prázdné místo v pruhu, ne chybějící data — proto má pruh vlastní
 * podklad. Sdílený crosshair čte všechny stroje najednou, aby šlo porovnat,
 * co dělaly ostatní ve chvíli, kdy jeden stál.
 */
export function ActivityTimeline({
  startAt,
  series,
  stripHeight = 62,
}: {
  startAt: string;
  series: ActivitySeries[];
  stripHeight?: number;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const count = series[0]?.hourly.length ?? 0;
  const W = 960;
  const L = 108;
  const R = 14;
  const TOP = 22;
  const GAP = 26;
  const plot = W - L - R;
  const pw = count > 0 ? plot / count : 0;
  const axisY = TOP + series.length * (stripHeight + GAP) - GAP + 22;
  const H = axisY + 12;

  /** Hodina začátku každého dne — svislice a popisek na ose. */
  const dayMarks = useMemo(() => {
    const start = new Date(startAt).getTime();
    const marks: { index: number; label: string }[] = [];
    for (let i = 0; i < count; i += 1) {
      const d = new Date(start + i * 3_600_000);
      if (d.getHours() === 0) {
        marks.push({ index: i, label: `${DAY_NAMES[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.` });
      }
    }
    return marks;
  }, [startAt, count]);

  const hoverLabel = useMemo(() => {
    if (hover == null) return null;
    const d = new Date(new Date(startAt).getTime() + hover * 3_600_000);
    return `${DAY_NAMES[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}. ${String(d.getHours()).padStart(2, "0")}:00`;
  }, [hover, startAt]);

  if (count === 0) return null;

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = ((e.clientX - box.left) / box.width) * W;
    const i = Math.floor((x - L) / pw);
    setHover(i >= 0 && i < count ? i : null);
  };

  return (
    <div className="relative overflow-x-auto">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full"
        style={{ minWidth: 620 }}
        role="img"
        aria-label="Hodinová obsazenost strojů"
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {series.map((s, r) => {
          const y0 = TOP + r * (stripHeight + GAP);
          const color = toneColor[s.tone];
          const worked = s.hourly.reduce((a, b) => a + b, 0) / 60;
          return (
            <g key={s.id}>
              <rect x={L} y={y0} width={plot} height={stripHeight} fill="var(--surface-inset)" />
              {dayMarks.map((m) => (
                <line
                  key={m.index}
                  x1={L + m.index * pw}
                  y1={y0}
                  x2={L + m.index * pw}
                  y2={y0 + stripHeight}
                  stroke="var(--border-subtle)"
                  strokeWidth={1}
                />
              ))}
              {s.hourly.map((min, i) =>
                min > 0 ? (
                  <rect
                    key={i}
                    x={L + i * pw}
                    y={y0 + stripHeight - Math.max(1.2, (min / 60) * stripHeight)}
                    width={Math.max(pw - 0.45, 0.9)}
                    height={Math.max(1.2, (min / 60) * stripHeight)}
                    fill={color}
                  />
                ) : null,
              )}
              <line
                x1={L}
                y1={y0 + stripHeight}
                x2={W - R}
                y2={y0 + stripHeight}
                stroke="var(--border-default)"
                strokeWidth={1}
              />
              <text
                x={L - 12}
                y={y0 + stripHeight - 16}
                textAnchor="end"
                fill="var(--text-primary)"
                fontSize={13}
                fontWeight={600}
              >
                {s.name}
              </text>
              <text x={L - 12} y={y0 + stripHeight - 2} textAnchor="end" fill={color} fontSize={11}>
                {fmtHours(worked)}
              </text>
              {s.note ? (
                <text x={L - 12} y={y0 + stripHeight + 12} textAnchor="end" fill="var(--text-tertiary)" fontSize={10}>
                  {s.note}
                </text>
              ) : null}
              <text x={L - 12} y={y0 + 9} textAnchor="end" fill="var(--text-tertiary)" fontSize={9}>
                60 min
              </text>
            </g>
          );
        })}

        {dayMarks.map((m) => (
          <g key={m.index}>
            <line
              x1={L + m.index * pw}
              y1={axisY - 13}
              x2={L + m.index * pw}
              y2={axisY - 8}
              stroke="var(--text-tertiary)"
              strokeWidth={1}
            />
            <text x={L + m.index * pw + 4} y={axisY} fill="var(--text-secondary)" fontSize={10.5}>
              {m.label}
            </text>
          </g>
        ))}

        {hover != null ? (
          <line
            x1={L + (hover + 0.5) * pw}
            y1={TOP}
            x2={L + (hover + 0.5) * pw}
            y2={TOP + (series.length - 1) * (stripHeight + GAP) + stripHeight}
            stroke="var(--text-primary)"
            strokeWidth={1}
            opacity={0.35}
            pointerEvents="none"
          />
        ) : null}
      </svg>

      {hover != null && hoverLabel ? (
        <div
          className="pointer-events-none absolute top-1 px-2.5 py-1.5 text-[11px] leading-relaxed"
          style={{
            left: `${((L + (hover + 0.5) * pw) / W) * 100}%`,
            transform: "translateX(-50%)",
            borderRadius: "var(--radius-box)",
            background: "var(--surface)",
            border: "1px solid var(--border-default)",
            boxShadow: "var(--shadow-panel)",
            color: "var(--text-secondary)",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ color: "var(--text-primary)", fontWeight: 600 }}>{hoverLabel}</div>
          {series.map((s) => (
            <div key={s.id}>
              <span style={{ color: toneColor[s.tone] }}>■</span> {s.name}: {s.hourly[hover] ?? 0} min
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export type CeilingRow = {
  id: string;
  name: string;
  tone: Tone;
  /** Kolik hodin práce tarif uveze za týden. */
  ceilingHours: number;
  note?: string;
};

/**
 * Strop tarifu proti 168 hodinám, které týden má. Ukazuje to, co z registru
 * workerů nejde vyčíst: žádný stroj neuveze celý týden, jen se liší tím,
 * jak brzy dojede.
 */
export function CeilingBars({ rows }: { rows: CeilingRow[] }) {
  const W = 960;
  const L = 108;
  const R = 128;
  const TOP = 34;
  const BH = 30;
  const GAP = 24;
  const MAX = 168;
  const plot = W - L - R;
  const H = TOP + rows.length * (BH + GAP) - GAP + 14;
  if (rows.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full"
        style={{ minWidth: 620 }}
        role="img"
        aria-label="Týdenní strop tarifu proti 168 hodinám"
      >
        {[0, 42, 84, 126, 168].map((v) => (
          <g key={v}>
            <line
              x1={L + (v / MAX) * plot}
              y1={TOP - 8}
              x2={L + (v / MAX) * plot}
              y2={H - 8}
              stroke={v === MAX ? "var(--border-default)" : "var(--border-subtle)"}
              strokeWidth={v === MAX ? 1.5 : 1}
            />
            <text
              x={L + (v / MAX) * plot}
              y={TOP - 15}
              textAnchor="middle"
              fill={v === MAX ? "var(--text-secondary)" : "var(--text-tertiary)"}
              fontSize={10.5}
            >
              {v === MAX ? "168 h = celý týden" : `${v} h`}
            </text>
          </g>
        ))}
        {rows.map((r, i) => {
          const y = TOP + i * (BH + GAP);
          const w = (Math.min(r.ceilingHours, MAX) / MAX) * plot;
          const color = toneColor[r.tone];
          return (
            <g key={r.id}>
              <rect x={L} y={y} width={plot} height={BH} fill="var(--surface-inset)" />
              <rect x={L} y={y} width={w} height={BH} fill={color} />
              <text x={L - 12} y={y + BH / 2 - 2} textAnchor="end" fill="var(--text-primary)" fontSize={13} fontWeight={600}>
                {r.name}
              </text>
              {r.note ? (
                <text x={L - 12} y={y + BH / 2 + 12} textAnchor="end" fill="var(--text-tertiary)" fontSize={10}>
                  {r.note}
                </text>
              ) : null}
              <text x={L + w + 10} y={y + BH / 2 + 4} fill="var(--text-primary)" fontSize={12} fontWeight={600}>
                {Math.round(r.ceilingHours)} h
              </text>
              <text x={L + w + 10} y={y + BH / 2 + 17} fill="var(--text-tertiary)" fontSize={10}>
                pak stojí {Math.max(0, Math.round(MAX - r.ceilingHours))} h
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
